import { Tabs as TabsPrimitive } from 'radix-ui';
import type { LucideIcon } from 'lucide-react';
import { createContext, useContext, useRef, useState, type ComponentProps } from 'react';
import { DialogLayoutContext } from './dialog-layout';
import { cn } from '../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

const ModalTabsContext = createContext<{value: string} | null>(null);

export function Tabs({ className, value, defaultValue, onValueChange, onInvalidCapture, modalSizing = true, ...props }: ComponentProps<typeof TabsPrimitive.Root> & { modalSizing?: boolean }) {
  const dialog = useContext(DialogLayoutContext);
  const modal = dialog && modalSizing;
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const selected = value ?? internalValue;
  const firstInvalid = useRef(false);
  const change = (next: string) => { if (value === undefined) setInternalValue(next); onValueChange?.(next); };
  return <ModalTabsContext.Provider value={modal ? {value: selected} : null}>
    <TabsPrimitive.Root data-slot="tabs" data-modal-tabs={modal ? '' : undefined} className={cn('flex flex-col gap-3', className)} value={selected} onValueChange={change} {...props}
      onInvalidCapture={event => {
        onInvalidCapture?.(event);
        if (!modal || event.defaultPrevented || !(event.target instanceof HTMLElement)) return;
        const target = event.target;
        const panel = target.closest<HTMLElement>('[data-modal-panel]');
        if (!panel || panel.closest('[data-slot="tabs"]') !== event.currentTarget) return;
        // Native validation checks mounted inactive controls too. Keep their values in FormData and reveal the first
        // invalid panel before focusing; later invalid controls must not steal that first error's focus.
        if (firstInvalid.current) { if (panel.hasAttribute('inert')) event.preventDefault(); return; }
        firstInvalid.current = true;
        queueMicrotask(() => {firstInvalid.current = false;});
        if (!panel.hasAttribute('inert')) return;
        event.preventDefault();
        change(panel.dataset.modalPanel!);
        requestAnimationFrame(() => {if (target.closest<HTMLElement>('[data-modal-panel]')?.hasAttribute('inert')) return; target.focus(); if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) target.reportValidity();});
      }} />
  </ModalTabsContext.Provider>;
}

export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List data-slot="tabs-list" className={cn('@container/tabs flex min-h-control shrink-0 items-stretch gap-1 overflow-x-auto border-b p-1', className)} {...props} />;
}

export interface TabsTriggerProps extends ComponentProps<typeof TabsPrimitive.Trigger> {
  icon?: LucideIcon;
  /** Translated accessible label; compact tabs show the icon with this label in a tooltip. */
  label?: string;
  /** Keep visible labels in compact form sections; responsive hiding remains the default. */
  labelVisibility?: 'responsive' | 'always';
  /** Marks a tab whose panel holds a validation error (dot indicator, `data-invalid`). Pair with `invalidLabel`. */
  invalid?: boolean;
  /** Translated screen-reader suffix announced for an invalid tab. */
  invalidLabel?: string;
}

export function TabsTrigger({ className, icon: Icon, label, labelVisibility = 'responsive', invalid = false, invalidLabel, children, onFocus, ...props }: TabsTriggerProps) {
  const trigger = (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        'inline-flex min-h-control shrink-0 items-center gap-1.5 border-b-2 border-transparent px-2 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-inset focus-visible:ring-ring/50 disabled:opacity-50 aria-selected:border-primary aria-selected:text-foreground',
        className,
      )}
      {...props}
      data-invalid={invalid ? '' : undefined}
      aria-label={label ?? props['aria-label']}
      onFocus={(event) => { onFocus?.(event); if (!event.defaultPrevented) event.currentTarget.scrollIntoView?.({block:'nearest',inline:'nearest'}); }}
    >
      {Icon && <Icon aria-hidden className="size-4 shrink-0" />}
      {label ? <span className={Icon && labelVisibility === 'responsive' ? 'sr-only @3xl/tabs:not-sr-only' : undefined}>{label}</span> : children}
      {invalid && <><span aria-hidden className="size-1.5 shrink-0 rounded-full bg-destructive" />{invalidLabel && <span className="sr-only">{invalidLabel}</span>}</>}
    </TabsPrimitive.Trigger>
  );
  return Icon && label ? <Tooltip><TooltipTrigger asChild>{trigger}</TooltipTrigger><TooltipContent>{label}</TooltipContent></Tooltip> : trigger;
}

/** One mounted instance per modal panel: inactive panels still size the grid but are inert and unannounced. */
export function TabsContent({ className, value, forceMount, style, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  const modal = useContext(ModalTabsContext);
  const inactive = modal !== null && modal.value !== value;
  return <TabsPrimitive.Content data-slot="tabs-content" data-modal-panel={modal ? value : undefined} value={value}
    forceMount={modal ? true : forceMount} {...props} inert={inactive || undefined} aria-hidden={inactive || props['aria-hidden']}
    className={cn('outline-none', className)} style={inactive ? {...style, visibility: 'hidden', pointerEvents: 'none'} : style} />;
}
